import { useCallback, useMemo } from 'react';

import { useOptionalItems } from '~/api/easy-estimator-api/optional-items-api/useOptionalItems';
import { IMAGE_COMING_SOON, NO_TEAR_OUT, UNIT_MEASURE } from '~/core/constants';
import { useShapeArea } from '~/core/hooks/useShapeArea';
import { TearOutModel } from '~/core/models';
import {
  formatPriceUnitOrFree,
  getProductDescription,
  imgUrl,
  isSomething,
} from '~/core/utils';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  decreaseSelectedTearOuts,
  increaseSelectedTearOuts,
  removeSelectedTearOut,
  selectSelectedTearOuts,
  updateSelectedTearOut,
} from '~/redux/slices/tearOut';

type TearOutDisplayData = {
  title: string;
  image: string;
  tearOutPrice: string;
  quantity: number;
  isSelected: boolean;
};

const useSidePanelTearouts = () => {
  const { tearOuts: allTearOuts, isFetchingOptionalItems } = useOptionalItems();
  const { totalBaseCountertopArea: countertopAreaQty } = useShapeArea();

  const selectedTearOuts = useAppSelector(selectSelectedTearOuts);

  const dispatch = useAppDispatch();

  const tearOuts = useMemo(
    () =>
      allTearOuts
        .filter(({ name }) => name !== NO_TEAR_OUT)
        .map<[TearOutModel, TearOutDisplayData]>((tearOut) => {
          const quantity = selectedTearOuts[tearOut.productId];

          return [
            tearOut,
            {
              title: getProductDescription(tearOut),
              image: imgUrl(tearOut.imageUrl ?? IMAGE_COMING_SOON, 300),
              tearOutPrice: formatPriceUnitOrFree(
                tearOut.price,
                tearOut.unitOfMeasure || UNIT_MEASURE.CORNER_RADIUS,
                true
              ),
              quantity: quantity ?? 0,
              isSelected: isSomething(quantity),
            },
          ];
        }),
    [allTearOuts, selectedTearOuts]
  );

  const decreaseSelectedTearOut = useCallback(
    (id: TearOutModel['productId']) => () => {
      if (selectedTearOuts[id] === 1) {
        dispatch(removeSelectedTearOut(id));
      } else {
        dispatch(decreaseSelectedTearOuts(id));
      }
    },
    [dispatch, selectedTearOuts]
  );

  const increaseSelectedTearOut = useCallback(
    (id: TearOutModel['productId']) => () => {
      const isThereAnySelectedTearOuts =
        Object.values(selectedTearOuts).reduce((acc, qty) => acc + qty, 0) > 0;

      if (isThereAnySelectedTearOuts) {
        dispatch(increaseSelectedTearOuts(id));
      } else {
        //if no tearouts selected start at countertop area qty
        dispatch(updateSelectedTearOut({ id, qty: countertopAreaQty }));
      }
    },

    [countertopAreaQty, dispatch, selectedTearOuts]
  );
  const setSelectedTearOutQty = useCallback(
    (id: TearOutModel['productId']) => (qty: number) => {
      if (qty === 0) {
        dispatch(removeSelectedTearOut(id));
      } else {
        dispatch(updateSelectedTearOut({ id, qty }));
      }
    },
    [dispatch]
  );
  const setAreaQtyOrDeselect = useCallback(
    (id: TearOutModel['productId']) => {
      const hasSelectedTearOuts =
        Object.values(selectedTearOuts).reduce((acc, qty) => acc + qty, 0) > 0;

      return () => {
        if (hasSelectedTearOuts) {
          if (isSomething(selectedTearOuts[id])) {
            dispatch(removeSelectedTearOut(id));
          } else {
            dispatch(updateSelectedTearOut({ id, qty: 0 }));
          }
        } else {
          dispatch(updateSelectedTearOut({ id, qty: countertopAreaQty }));
        }
      };
    },
    [countertopAreaQty, dispatch, selectedTearOuts]
  );

  return {
    tearOuts,
    isFetchingOptionalItems,
    setAreaQtyOrDeselect,
    setSelectedTearOutQty,
    increaseSelectedTearOut,
    decreaseSelectedTearOut,
  };
};

export default useSidePanelTearouts;
